/**
 * Structured logger for production error monitoring.
 * Outputs JSON in production, readable format in development.
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

const IS_PROD = import.meta.env.PROD;
const LOG_LEVELS: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };
const MIN_LEVEL: LogLevel = IS_PROD ? "warn" : "debug";

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LEVEL];
}

function formatEntry(entry: LogEntry): string {
  if (IS_PROD) return JSON.stringify(entry);
  const prefix = `[${entry.level.toUpperCase()}] ${entry.timestamp}`;
  return entry.context
    ? `${prefix} ${entry.message} ${JSON.stringify(entry.context)}`
    : `${prefix} ${entry.message}`;
}

function createEntry(level: LogLevel, message: string, context?: Record<string, unknown>, err?: Error): LogEntry {
  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
  };
  if (context) entry.context = context;
  if (err) entry.error = { name: err.name, message: err.message, stack: err.stack };
  return entry;
}

function log(level: LogLevel, message: string, context?: Record<string, unknown>, err?: Error) {
  if (!shouldLog(level)) return;
  const entry = createEntry(level, message, context, err);
  const output = formatEntry(entry);
  const consoleFn = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
  consoleFn(output);
}

export const logger = {
  debug: (message: string, context?: Record<string, unknown>) => log("debug", message, context),
  info: (message: string, context?: Record<string, unknown>) => log("info", message, context),
  warn: (message: string, context?: Record<string, unknown>) => log("warn", message, context),
  error: (message: string, err?: Error, context?: Record<string, unknown>) => log("error", message, context, err),
};

/**
 * Global unhandled error/rejection catcher.
 * Call once at app startup.
 */
export function initGlobalErrorHandlers() {
  window.addEventListener("error", (event) => {
    logger.error("Unhandled error", event.error instanceof Error ? event.error : new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    logger.error("Unhandled promise rejection", err);
  });
}
